import { Diagnosis } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Diagnosis";
import { DiagnosisHelper } from "@/Patient/Helpers/DiagnosisHelper";
import { DiagnosisModel } from "@/Patient/Models/MedicalSummary/DiagnosisModel";
import { DiagnosisSearchAutocomplete, DiagnosisSearchListItem, IDiagnosisSearchAutocomplete } from "./DiagnosisSearchAutocomplete";
import FavoriteDiagnosisSearchItem from "./FavoriteDiagnosisSearchItem";

import styles from "./FavoriteDiagnosisAutocompleteControl.module.css";

class FavoriteDiagnosisAutocompleteControl extends DiagnosisSearchAutocomplete {
    constructor(props: IDiagnosisSearchAutocomplete) {
        super(props);

        this.state = {
            ...this.state,
            openedByClick: true
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.input?.focus?.();
        document.addEventListener("click", this.handleClick.bind(this));
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        document.removeEventListener("click", this.handleClick.bind(this));
    }

    public render(): JSX.Element {
        const siteModel = cacheStore.GetSiteById(this.props.siteId);
        const favoriteRefs: { [id: string]: HTMLLIElement } = {};
        const listRefs: { [id: string]: HTMLLIElement } = {};

        const handleKeyDown = (): void => {
            const favoritesActiveItem = this.state.favorites.find(item => item.isActive);
            const listActiveItem = this.state.list.toArray().find(item => item.Active);

            if (favoritesActiveItem) {
                favoriteRefs[favoritesActiveItem.id].scrollIntoView({
                    block: "center"
                });
            }
            else if (listActiveItem) {
                listRefs[listActiveItem.Id].scrollIntoView({
                    block: "center"
                });
            }
        };

        return (
            <div className="fmp-autocomplete">
                <input
                    ref={ref => this.input = ref}
                    id={this.props.id}
                    data-cy={this.props.dataCy}
                    type="text"
                    value={this.state.text}
                    className={this.props.valid === false ? "form-control invalid" : "form-control"}
                    onBlur={() => this.onBlur()}
                    onFocus={() => this.onFocus()}
                    onKeyDown={e => {
                        handleKeyDown();
                        this.inputKeyDown(e);
                    }}
                    onChange={e => this.onTextChange(e)}
                    autoComplete="off"
                />
                {(this.state.favorites?.length > 0 || this.state.list.size > 0) && !this.state.forceHideList && (
                    <div className="list-container-txtbox">
                        <div
                            className="results-container"
                            data-cy={this.props.dataCy ? `${this.props.dataCy}-results` : null}
                            style={{ minWidth: "150px" }}
                            onMouseEnter={() => this.mutateState(s => s.cancelOnblur = true)}
                            onMouseLeave={() => this.mutateState(s => s.cancelOnblur = false)}
                        >
                            <ul className="results-list">
                                {this.state.favorites?.map((item: DiagnosisSearchListItem) => {
                                    const divStyle: React.CSSProperties = item.isActive == true ? { backgroundColor: "#f4f6f9" } : {};
                                    const hyperlinkStyle: React.CSSProperties = item.isActive == true ? { background: "lightblue" } : {};

                                    return (
                                        <li
                                            ref={ref => favoriteRefs[item.id] = ref}
                                            key={"favorite_" + item.id}
                                            onClick={() => this.selectFavoriteItem(item.favoriteItem)}
                                        >
                                            <FavoriteDiagnosisSearchItem
                                                isFavorite={item.isFavorite}
                                                id={item.id}
                                                item={item.favoriteItem}
                                                onFavoriteToggleClick={() => this.mutateState(s => s.openedByClick = false)}
                                                style={divStyle}
                                                hyperlinkStyle={hyperlinkStyle}
                                            />
                                        </li>
                                    );
                                })}
                                {(this.state.favorites?.length > 0 && this.state.list.size > 0) && <hr className={styles.divider} />}
                                {this.state.list?.map(
                                    (item: Diagnosis, i: number) => {
                                        const displayLocal = item.getLocaleFromSite(siteModel);
                                        const itemName = item.getName(displayLocal);

                                        const divStyle: React.CSSProperties = item.Active == true ? { backgroundColor: "#f4f6f9" } : {};
                                        const hyperlinkStyle: React.CSSProperties = item.Active == true ? { background: "lightblue" } : {};

                                        return (
                                            <li
                                                ref={ref => listRefs[item.Id] = ref}
                                                key={i + "__code_"}
                                                onClick={this.selectItem.bind(this, item)}
                                            >
                                                <div className="flex-container" style={divStyle}>
                                                    {item.Code && (
                                                        <a style={hyperlinkStyle}>
                                                            <span>{`${item.Code} ${itemName} - `}</span>
                                                            <span style={{ fontStyle: "italic" }}>{DiagnosisHelper.getSourceNameFromCode(item.Type)}</span>
                                                        </a>
                                                    )}
                                                    {!item.Code && (
                                                        <a style={hyperlinkStyle}>
                                                            <span>{itemName}</span>
                                                        </a>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    }
                                )}

                            </ul>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    protected override async loadDataRaw(text: string): Promise<void> {
        await super.loadDataRaw(text);

        this.mutateState(s => {
            s.favorites = this.getSearchedFavorites(text);

            if (s.favorites.length > 0) {
                const collection = Immutable.List(s.list?.toArray()?.map(item => (new Diagnosis({ ...item, Active: false }))));
                s.list = collection;
            }
        });
    }

    protected override inputKeyDown(e: React.KeyboardEvent): void {
        if (e.keyCode == 13) {
            if (this.state.forceHideList == true ||
                (this.state.list?.count() === 0 && this.state.favorites?.length === 0)
            ) {
                this.forceHideList(() => this.loadData(this.state.text));
            }
            else {
                this.addSelectedItem();
            }
        }
        else {
            super.inputKeyDown(e);
        }
    }

    protected override moveSelection(isDirectionUp: boolean): void {
        this.mutateState(s => {
            const { collection, forceHide, favorites = [] } = this.moveSelectionUpDown(s.list.toArray(), s.favorites, isDirectionUp);
            s.forceHideList = forceHide;
            s.list = Immutable.List(collection);
            s.favorites = favorites;
        });
    }

    // keyboard triggered
    private moveSelectionUpDown(collection: Diagnosis[], favorites: DiagnosisSearchListItem[], isDirectionUp: boolean): { collection: Diagnosis[], forceHide: boolean, favorites?: DiagnosisSearchListItem[] } {
        // The dropdown combines the favorites and collections, in that order. This code moves a
        // synthetic index within that combined list to find the new active item.

        // skip empty list
        if (!collection.any() && !favorites.any())
            return { collection, favorites, forceHide: true };

        // get indexes & reset flags
        const lastIndex = favorites.length + collection.length - 1;
        const minCollectionIndex = favorites.length;
        let activeIndex: number | null = null;
        for (let i = 0; i < favorites.length; i++) {
            if (favorites[i].isActive) {
                activeIndex = i;
                favorites[i].isActive = false;
            }
        }

        for (let i = 0; i < collection.length; i++) {
            if (collection[i].Active) {
                activeIndex = minCollectionIndex + i;
                collection[i].Active = false;
            }
        }

        // if a value was active, don't allow moving past the ends
        if (activeIndex === (isDirectionUp ? 0 : lastIndex))
            return { collection, favorites, forceHide: true };

        activeIndex ??= -1;
        // move index
        activeIndex += isDirectionUp ? -1 : 1;
        if (activeIndex < 0)
            activeIndex = lastIndex;
        else if (activeIndex > lastIndex)
            activeIndex = 0;

        // mark active
        if (activeIndex < minCollectionIndex)
            favorites[activeIndex].isActive = true;
        else
            collection[activeIndex - minCollectionIndex].Active = true;

        return { collection, favorites, forceHide: false };
    }

    protected override async onFocus(): Promise<void> {
        if (IsNullOrUndefined(this.state.text) || this.state.text.length < 2) {
            // reset hide result list state
            this.mutateState(s => {
                s.list = Immutable.List([]);
                s.forceHideList = false;
                s.favorites = this.getAllFavorites();
                s.openedByClick = true;
            });
        }
        else {
            await this.loadDataRaw(this.state.text);
        }
    }

    private getAllFavorites(): DiagnosisSearchListItem[] {
        if (this.props.disableFavorites)
            return [];

        let favorites: DiagnosisSearchListItem[] = [];
        if (cacheStore.FavoriteDiagnoses) {
            const tmpFavorites: DiagnosisModel[] = cacheStore.FavoriteDiagnoses.toArray();

            const uniqueDiagnosesIds = Object
                .keys(tmpFavorites?.map(p => p.Model.Id)?.filter(id => !!id)?.toDictionary(id => id) || {});

            for (let diagnosisId of uniqueDiagnosesIds) {
                const items = cacheStore.FavoriteDiagnoses.filter(p => p.Model.Id == diagnosisId);

                favorites.push(new DiagnosisSearchListItem({
                    id: diagnosisId,
                    isFavorite: true,
                    favoriteItem: items.first()
                }));
            }
        }

        return favorites;
    }

    private getSearchedFavorites(searchText: string): DiagnosisSearchListItem[] {
        if (this.props.disableFavorites || (cacheStore.FavoriteDiagnoses?.isEmpty() ?? true))
            return [];

        let results = cacheStore.FavoriteDiagnoses
            .toArray()
            .filter(diagnosis => diagnosis.Model.Id);

        if (!IsNullOrWhiteSpace(searchText))
            results = results.filterSearch(searchText, diagnosis => diagnosis.GetName());

        return results
            .map((diagnosis, index) => new DiagnosisSearchListItem({
                id: diagnosis.Model.Id,
                isFavorite: true,
                isActive: index == 0,
                favoriteItem: diagnosis
            }));
    }

    protected override addSelectedItem(): void {
        const favorite = this.state.favorites.firstOrDefault(item => item.isActive);
        const item = this.state.list.toArray().firstOrDefault(item => item.Active);

        if (favorite) {
            this.selectFavoriteItem(favorite.favoriteItem);
        }
        else if (item) {
            super.addSelectedItem();
        }
    }

    protected selectFavoriteItem(model: DiagnosisModel): void {
        this.mutateState(
            s => {
                s.forceHideList = true;
                s.list = Immutable.List([]);
                s.text = this.props.clearTextAfterSelect
                    ? ""
                    : `${model.Model.DiagnosisItems[0].Code} - ${model.Model.DiagnosisItems[0].Text}`;
                s.cancelOnblur = true;
            },
            () => {
                // wait until state has been set to call parent select item callback
                this.props.onFavoriteItemSelect?.(model);
                this.input?.focus?.();
                this.input?.select?.();

                this.mutateState(s => s.cancelOnblur = false);
            }
        );
    }

    public handleClick(): void {
        if (!this.state.openedByClick) {
            this.mutateState(s => {
                s.forceHideList = true;
                s.openedByClick = false;
            });
        }
    }
}

export { FavoriteDiagnosisAutocompleteControl };
